/**
 * Fullscreen for the viewer container.
 *
 * Desktop browsers, Android and iPad support the Fullscreen API on any element. iPhone Safari
 * does not: only <video> elements can go fullscreen there. On those browsers the viewer can
 * fall back to a "pseudo" fullscreen that pins the container over the whole window (fixed,
 * 100dvh, above the page). That fallback is behind ENABLE_PSEUDO_FULLSCREEN (off, see there);
 * with it off, browsers without the API simply don't get a fullscreen button.
 */

/**
 * Feature flag: fill the window when the browser has no Fullscreen API for the container.
 *
 * Off: the viewer already fills its own window (FILLMODE_FILL_WINDOW), and every StorySplat
 * scene runs in a window of its own (the /api/v2-html page, full-page or in an iframe). Pinning the
 * container over that window changes nothing visible, and a page can neither hide Safari's
 * toolbars nor grow its iframe over the host page. Turning this on pays off only together with a
 * host-page helper that enlarges the iframe when asked (postMessage), which does not exist yet.
 */
export const ENABLE_PSEUDO_FULLSCREEN = false;

export const PSEUDO_FULLSCREEN_CLASS = 'storysplat-pseudo-fullscreen';
/** Set on <html> while pseudo fullscreen is on, to stop the page behind from scrolling. */
export const PSEUDO_FULLSCREEN_ROOT_CLASS = 'storysplat-pseudo-fullscreen-root';

export type FullscreenMode = 'native' | 'pseudo' | 'none';

interface FullscreenCapableElement {
    requestFullscreen?: (options?: FullscreenOptions) => Promise<void> | void;
    webkitRequestFullscreen?: () => Promise<void> | void;
}

interface FullscreenCapableDocument {
    fullscreenEnabled?: boolean;
    webkitFullscreenEnabled?: boolean;
}

/**
 * How the fullscreen button can work for `element`: the real Fullscreen API when the browser
 * offers it (feature detection, not the user agent, so iPads keep real fullscreen), the window-
 * filling fallback when the flag allows it, or not at all.
 */
export function detectFullscreenMode(
    element: FullscreenCapableElement | null | undefined,
    doc: FullscreenCapableDocument | null | undefined,
    allowPseudo: boolean = ENABLE_PSEUDO_FULLSCREEN,
): FullscreenMode {
    const hasMethod = !!element && (typeof element.requestFullscreen === 'function' || typeof element.webkitRequestFullscreen === 'function');
    // Browsers that ship the method but disallow it (iPhone Safari 16.4+, some embeds) report
    // fullscreenEnabled === false. Undefined means an older engine with only the method.
    const enabledFlags = [doc?.fullscreenEnabled, doc?.webkitFullscreenEnabled].filter((v) => typeof v === 'boolean');
    const enabled = enabledFlags.length === 0 ? true : enabledFlags.some(Boolean);
    if (hasMethod && enabled) return 'native';
    return allowPseudo ? 'pseudo' : 'none';
}

export interface PseudoFullscreenController {
    isActive(): boolean;
    enter(): void;
    exit(): void;
    toggle(): boolean;
    /** Leaves pseudo fullscreen and removes listeners. */
    destroy(): void;
}

/**
 * Window-filling fallback. Entering adds a class that pins `container` over the viewport and
 * locks page scroll; Escape (when there is a keyboard) or toggle() leaves it. `onChange` runs
 * after every switch so the caller can swap the button icon. A resize event is dispatched so
 * the renderer picks up the new canvas size.
 */
export function createPseudoFullscreen(
    container: HTMLElement,
    onChange?: (active: boolean) => void,
    win: Window = window,
): PseudoFullscreenController {
    const doc = container.ownerDocument;
    let active = false;
    let savedScroll = 0;
    const notifyResize = () => {
        try {
            win.dispatchEvent(new Event('resize'));
        } catch {
            /* old engines without the Event constructor */
        }
    };
    const set = (next: boolean) => {
        if (next === active) return;
        active = next;
        if (active) {
            savedScroll = win.scrollY || 0;
            container.classList.add(PSEUDO_FULLSCREEN_CLASS);
            doc.documentElement.classList.add(PSEUDO_FULLSCREEN_ROOT_CLASS);
        } else {
            container.classList.remove(PSEUDO_FULLSCREEN_CLASS);
            doc.documentElement.classList.remove(PSEUDO_FULLSCREEN_ROOT_CLASS);
            // Restore the page position the lock may have disturbed (only when it moved).
            if ((win.scrollY || 0) !== savedScroll) {
                try {
                    win.scrollTo(0, savedScroll);
                } catch {
                    /* some embeds don't implement scrollTo */
                }
            }
        }
        notifyResize();
        onChange?.(active);
    };
    const onKeyDown = (e: KeyboardEvent) => {
        if (active && e.key === 'Escape') set(false);
    };
    doc.addEventListener('keydown', onKeyDown);
    return {
        isActive: () => active,
        enter: () => set(true),
        exit: () => set(false),
        toggle: () => {
            set(!active);
            return active;
        },
        destroy: () => {
            set(false);
            doc.removeEventListener('keydown', onKeyDown);
        },
    };
}

/** CSS for the fallback; injected with the rest of the viewer styles. */
export const PSEUDO_FULLSCREEN_CSS = `
    .${PSEUDO_FULLSCREEN_CLASS} {
      position: fixed !important;
      inset: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      height: 100dvh !important;
      max-width: none !important;
      max-height: none !important;
      margin: 0 !important;
      border-radius: 0 !important;
      z-index: 2147483000 !important;
    }
    html.${PSEUDO_FULLSCREEN_ROOT_CLASS},
    html.${PSEUDO_FULLSCREEN_ROOT_CLASS} body {
      overflow: hidden !important;
      overscroll-behavior: none;
    }
`;

/**
 * Hide a button group (the top-right pill) when none of its children is visible, and show it
 * again when one is. Buttons can be hidden after the group is built (fullscreen on browsers
 * that can't do it, photo/share toggled later), which used to leave an empty chip behind.
 */
export function syncButtonGroupVisibility(group: HTMLElement | null | undefined): void {
    if (!group) return;
    const anyVisible = Array.from(group.children).some((child) => (child as HTMLElement).style?.display !== 'none');
    group.style.display = anyVisible ? '' : 'none';
}
